import { useMemo } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import AccountingStats from "@/pages/admin/contabilidad/components/AccountingStats";
import CapitalProjection from "@/pages/admin/contabilidad/components/CapitalProjection";
import ChannelPanel from "@/pages/admin/contabilidad/components/ChannelPanel";
import MonthlyProfitPanel from "@/pages/admin/contabilidad/components/MonthlyProfitPanel";
import FundsPanel from "@/pages/admin/contabilidad/components/FundsPanel";
import PurchasesPanel from "@/pages/admin/contabilidad/components/PurchasesPanel";
import ExpensesPanel from "@/pages/admin/contabilidad/components/ExpensesPanel";
import ExpenseWallets from "@/pages/admin/contabilidad/components/ExpenseWallets";
import WalletBalances from "@/pages/admin/contabilidad/components/WalletBalances";
import { buildAccounting, buildFunds, buildMonthlyProfit } from "@/utils/accounting";
import { buildPvSummary } from "@/utils/pv";
import type { Wallet } from "@/types";
import CostErrorPanel from "@/pages/admin/contabilidad/components/CostErrorPanel";
import PvPanel from "@/pages/admin/contabilidad/components/PvPanel";

const ContabilidadPage = () => {
  const {
    data,
    addExpense,
    deleteExpense,
    updateSettings,
    addFundTransfer,
    removeFundMovement,
    payExpenseFromFund,
    payExpenseWithLoan,
    addPurchaseToStock,
  } = useApp();

  const summary = useMemo(() => buildAccounting(data), [data]);
  const months = useMemo(() => buildMonthlyProfit(data), [data]);
  const funds = useMemo(() => buildFunds(data), [data]);
  const pv = useMemo(() => buildPvSummary(data), [data]);
  const operativos = useMemo(
    () => data.expenses.filter((e) => e.kind !== "compra"),
    [data.expenses]
  );
  const compras = useMemo(
    () => data.expenses.filter((e) => e.kind === "compra"),
    [data.expenses]
  );

  return (
    <div className="pb-24">
      <SectionTitle
        text="Contabilidad, Capital y Proyección"
        subtitle="Cuánto tienes, cuánto has ganado realmente, cómo se reparte la utilidad en fondos y qué compras están por pagar. Las ventas alimentan los fondos; las compras se pagan desde ellos."
      />

      <AccountingStats summary={summary} />

      <div className="mt-5">
        <FundsPanel
          funds={funds}
          movements={data.settings.fundMovements}
          onTransfer={addFundTransfer}
          onRemoveMovement={removeFundMovement}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-5">
        <CapitalProjection summary={summary} />
        <ChannelPanel summary={summary} />
      </div>

      <div className="mt-5">
        <CostErrorPanel products={data.products} />
      </div>

      <div className="mt-5">
        <PvPanel
          summary={pv}
          onSaveReferral={(monthKey, value) =>
            updateSettings({
              referralPvByMonth: { ...(data.settings.referralPvByMonth ?? {}), [monthKey]: value },
            })
          }
        />
      </div>

      <div className="mt-5">
        <MonthlyProfitPanel months={months} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-5">
        <WalletBalances
          balances={data.settings.walletBalances}
          expenses={data.expenses}
          onSave={(w: Wallet, v: number) =>
            updateSettings({
              walletBalances: { ...data.settings.walletBalances, [w]: v },
            })
          }
        />
        <ExpenseWallets expenses={operativos} />
      </div>

      <div className="mt-5">
        <PurchasesPanel
          purchases={compras}
          products={data.products}
          funds={funds}
          onPayFromFund={payExpenseFromFund}
          onPayWithLoan={payExpenseWithLoan}
          onPurchase={addPurchaseToStock}
          onDelete={deleteExpense}
        />
      </div>

      <div className="mt-5">
        <ExpensesPanel expenses={operativos} onAdd={addExpense} onDelete={deleteExpense} />
      </div>
    </div>
  );
};

export default ContabilidadPage;